import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useSWR from 'swr'
import {
  dismissTodayModule,
  fetchTodayContext,
  TodayAuthError,
  type ModuleId,
  type TodayContext,
} from '@/lib/bff/today'

/** Modules poll every 60 seconds (VIS-257). */
export const TODAY_POLL_MS = 60_000

/*
 * Loads the page-level Today context (access decision, venues, composed module
 * list and today's tenant dismissals) for the active tenant + venue scope. The
 * request only runs with an authenticated session and an active tenant, is
 * keyed by tenant + venue so switching either refetches, and polls every 60s so
 * dismissals made by other users in the tenant surface without a manual reload.
 */
export function useTodayContext(tenantId: string | null, venueId: string) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()

  const { data, error, isLoading, mutate } = useSWR<TodayContext>(
    isAuthenticated && tenantId ? ['bff/today/context', tenantId, venueId] : null,
    async ([, id, venue]: [string, string, string]) => {
      const token = await getAccessTokenSilently()
      if (!token) throw new TodayAuthError('No access token available')
      return fetchTodayContext(token, id, venue)
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      refreshInterval: TODAY_POLL_MS,
    },
  )

  return {
    context: data,
    error,
    isLoading,
    retry: () => mutate(),
    mutate,
  }
}

/*
 * Returns a dismiss action that records a module dismissal against the tenant
 * for the current day and optimistically suppresses it, then revalidates the
 * context so the server-recorded dismissal is reflected everywhere.
 */
export function useDismissTodayModule(
  tenantId: string | null,
  mutate: ReturnType<typeof useTodayContext>['mutate'],
) {
  const { getAccessTokenSilently } = useAuth0()

  return React.useCallback(
    async (module: ModuleId) => {
      if (!tenantId) return
      // Optimistically add the module to the dismissed set.
      await mutate(
        (current) =>
          current
            ? { ...current, dismissed: Array.from(new Set([...current.dismissed, module])) }
            : current,
        { revalidate: false },
      )
      try {
        const token = await getAccessTokenSilently()
        if (!token) throw new TodayAuthError('No access token available')
        await dismissTodayModule(token, tenantId, module)
      } finally {
        // Reconcile with the server whether the write succeeded or not.
        await mutate()
      }
    },
    [tenantId, mutate, getAccessTokenSilently],
  )
}
